"use client";

import { useTranslations } from "@/lib/compat/i18n";
import clsx from "clsx";
import type { CdmEvent, EventLogState } from "@/lib/sse/useSSE";
import { BankBadge } from "@/components/banks/BankBadge";
import { Monogram } from "@/components/persona/Monogram";
import { InterconnectedWeb } from "@/components/web/InterconnectedWeb";
import type { GraphState } from "@/components/web/types";
import { useMoneyFormatter } from "@/lib/i18n/format";

/**
 * Screen 6.1 — Final Reveal. The full network canvas is the protagonist —
 * this scene mounts its own InterconnectedWeb behind a translucent
 * overlay carrying the counter strip, trade cards, persona+bank hubs,
 * and the closing line. Single-screen layout per cofounder feedback
 * (the earlier 6.1a/6.1b split was reverted).
 *
 * A small contact-line footer was added during the UX review batch and
 * is retained — it closes the audience-to-followup loop without taking
 * vertical real estate from the network panel.
 */
export function FinalReveal({
  ev,
  graph,
  className,
}: {
  ev: EventLogState;
  graph?: GraphState;
  className?: string;
}) {
  // English-only after the L1 Phase 3 cutover (AR/PT bundles removed);
  // RTL is always false. Future locale work would re-introduce these.
  const rtl = false;
  const t       = useTranslations("finalReveal");
  const tLegal  = useTranslations("legalFooter");
  const fmtUsd  = useMoneyFormatter();

  const lcs = ev.events.filter((e: CdmEvent) => e.eventType === "lc.Issued");
  const hasLcs = lcs.length > 0;
  // Only fall through to canonical defaults when at least one LC has fired —
  // a stalled SSE stream should NOT render the canonical 4 trades / $6.03M
  // figures silently (that masks the bug). When lcs.length === 0, the
  // counter strip + trade cards hide entirely; the canvas + personas row +
  // closing line carry the scene.
  const tradeCount   = hasLcs ? lcs.length : 4;
  const totalVolume  = hasLcs
    ? lcs.reduce((sum, e) => sum + ((e.payload.amountUsd as number) || 0), 0)
    : 6_030_000;

  // Per-trade day stats — single source of truth. Counter strip totals are
  // derived sums; if you change a per-trade number, the headline totals
  // update automatically. Canonical sources: T1 from t1-onboarding.json
  // (wallClockSimulatedDays 11, preOrbitraEquivalentDays 42) + T1Reveal;
  // T2 from T2Reveal.tsx (9 / 38); T3 is bulk-commodity reverse corridor;
  // T4 is air-cargo fast-track (sub-week settlement).
  const T1_DAYS = { simulated: 11, preOrbitra: 42 } as const;
  const T2_DAYS = { simulated:  9, preOrbitra: 38 } as const;
  const T3_DAYS = { simulated: 10, preOrbitra: 30 } as const;
  const T4_DAYS = { simulated:  4, preOrbitra: 21 } as const;
  const totalSimulatedDays  = T1_DAYS.simulated  + T2_DAYS.simulated  + T3_DAYS.simulated  + T4_DAYS.simulated;
  const totalPreOrbitraDays = T1_DAYS.preOrbitra + T2_DAYS.preOrbitra + T3_DAYS.preOrbitra + T4_DAYS.preOrbitra;

  const alMadar    = ev.reputation["orbid:5493-almadar-ae"]      ?? null;
  const aurora     = ev.reputation["orbid:5493-aves-aurora-br"]  ?? null;
  const pampagro   = ev.reputation["orbid:5493-pampagro-br"]     ?? null;
  const gulfPhosph = ev.reputation["orbid:5493-gulfphosphate-ae"] ?? null;
  const verdeCarnes = ev.reputation["orbid:5493-verdecarnes-br"] ?? null;
  const alBayt      = ev.reputation["orbid:5493-albayt-ae"]      ?? null;

  return (
    <div className={clsx("flex h-full flex-col gap-2 p-3", className)}>
      {hasLcs && (
        <>
          {/* Counter strip — flex-wrap so a long counterCompounding translation
              (AR/PT) breaks to a second line cleanly instead of overflowing
              the strip. */}
          <div className="glass shrink-0 self-center px-5 py-2 shadow-glass">
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 font-mono text-[11px]">
              <Counter big={String(tradeCount)} unit={t("counterTrades")} />
              <Sep />
              <Counter big={fmtUsd(totalVolume)} unit={t("counterVolume")} />
              <Sep />
              <Counter big={`${totalSimulatedDays}d`} unit={t("counterDays")} />
              <Sep />
              <Counter big={`${totalPreOrbitraDays}d`} unit={t("counterPreOrbitra")} />
              <Sep />
              <span className="font-display text-[11px] uppercase tracking-[0.18em] text-state-financed">
                {t("counterCompounding")}
              </span>
            </div>
          </div>

          {/* Per-trade summary cards — 2×2 grid so each card has room
           *  to breathe + the persona row below doesn't have to share
           *  horizontal real estate with 4 narrow cards. */}
          <div className="grid shrink-0 grid-cols-2 gap-2 self-stretch">
            <TradeCard
              tag={t("tradeT1Tag")}
              parties="Aurora · BR  ↔  Al Madar · AE"
              commodity={t("tradeT1Commodity")}
              amount={fmtUsd(850_000)}
              finance={t("tradeT1Finance")}
              highlight={t("tradeT1Highlight")}
              simulatedDays={T1_DAYS.simulated}
              preOrbitraDays={T1_DAYS.preOrbitra}
            />
            <TradeCard
              tag={t("tradeT2Tag")}
              parties="Aurora · BR  ↔  Al Madar · AE"
              commodity={t("tradeT2Commodity")}
              amount={fmtUsd(1_500_000)}
              finance={t("tradeT2Finance")}
              highlight={t("tradeT2Highlight")}
              simulatedDays={T2_DAYS.simulated}
              preOrbitraDays={T2_DAYS.preOrbitra}
            />
            <TradeCard
              tag={t("tradeT3Tag")}
              parties="Pampagro · BR  ↔  Gulf Phosphate · AE"
              commodity={t("tradeT3Commodity")}
              amount={fmtUsd(3_400_000)}
              finance={t("tradeT3Finance")}
              highlight={t("tradeT3Highlight")}
              simulatedDays={T3_DAYS.simulated}
              preOrbitraDays={T3_DAYS.preOrbitra}
            />
            <TradeCard
              tag={t("tradeT4Tag")}
              parties="Verde Carnes · BR  ↔  Al Bayt · AE"
              commodity={t("tradeT4Commodity")}
              amount={fmtUsd(280_000)}
              finance={t("tradeT4Finance")}
              highlight={t("tradeT4Highlight")}
              simulatedDays={T4_DAYS.simulated}
              preOrbitraDays={T4_DAYS.preOrbitra}
            />
          </div>
        </>
      )}

      {/* InterconnectedWeb — promoted from background overlay to its own
       *  named panel between the trade cards and the personas row. flex-1
       *  fills remaining space; min-h floor keeps it visible even when the
       *  scene compresses on shorter viewports. */}
      {graph && (
        <div className="relative flex-1 min-h-[140px] overflow-hidden rounded-md border border-state-financed/30 bg-orbitra-ink/55">
          <div className="absolute inset-0">
            <InterconnectedWeb graph={graph} rtl={rtl} className="h-full w-full" />
          </div>
          <p className="absolute start-3 top-2 z-10 rounded bg-orbitra-ink/80 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">
            {t("networkPanelTitle")}
          </p>
        </div>
      )}

      {/* Personas + bank hubs row — personas in a 2×3 grid so 6 hubs
       *  don't overflow on narrow viewports. Banks block sits beside it
       *  with its own column. */}
      <div className="flex shrink-0 items-start justify-between gap-2 self-stretch">
        <div className="glass flex flex-col gap-1 px-3 py-2">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("personasTitle")}</p>
          <div className="grid grid-cols-3 gap-x-4 gap-y-1">
            <PersonaBadge name="Aurora"          full="Aurora Aves Brasil"          flag="BR" stats={aurora}      hint={t("personaAuroraHint")} />
            <PersonaBadge name="Al Madar"        full="Al Madar Foods"              flag="AE" stats={alMadar}     hint={t("personaAlMadarHint")} />
            <PersonaBadge name="Pampagro"        full="Pampagro Agronegócios Ltda." flag="BR" stats={pampagro}    hint={t("personaPampagroHint")} />
            <PersonaBadge name="Gulf Phosphate"  full="Gulf Phosphate UAE"          flag="AE" stats={gulfPhosph}  hint={t("personaGulfPhosphateHint")} />
            <PersonaBadge name="Verde Carnes"    full="Verde Carnes Brasil S.A."    flag="BR" stats={verdeCarnes} hint={t("personaVerdeCarnesHint")} />
            <PersonaBadge name="Al Bayt"         full="Al Bayt Premium Meats LLC"   flag="AE" stats={alBayt}      hint={t("personaAlBaytHint")} />
          </div>
        </div>

        <div className="glass flex flex-col gap-1 px-3 py-2">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">{t("banksTitle")}</p>
          <div className="flex items-center gap-3">
            <BankBadge name="FAB"  fundingPct={30} variant="compact" />
            <BankBadge name="Itau" fundingPct={50} variant="compact" />
            <span className="font-mono text-[9px] uppercase tracking-wider text-orbitra-muted">
              {t("settlementHidden")}
            </span>
          </div>
          <p className="text-[8pt] leading-snug text-orbitra-muted">{tLegal("bankBadge")}</p>
        </div>
      </div>

      {/* Closing line — narrower max-width centered so the network panel
       *  above keeps the lion's share of vertical real estate, and the page
       *  fits a typical 900px presenter viewport without scrolling. The
       *  contact line below closes the audience-to-followup loop without
       *  taking extra vertical real estate. */}
      <div className="glass shrink-0 w-full max-w-3xl self-center px-4 py-2 text-center">
        <p className="font-mono text-[8px] uppercase tracking-[0.32em] text-state-financed">{t("closingTitle")}</p>
        <p className="mt-1 font-display text-[15px] font-semibold leading-tight text-orbitra-paper sm:text-[17px]">
          {t("closingLineTop")} {t("closingLineMid")}
        </p>
        <p className="mt-0.5 font-display text-[12px] font-semibold tracking-tight text-state-financed">
          {t("closingLineCap")}
        </p>
      </div>
    </div>
  );
}

function Counter({ big, unit }: { big: string; unit: string }) {
  return (
    <span className="flex items-baseline gap-2">
      <span className="font-display text-2xl font-semibold text-state-financed">{big}</span>
      <span className="text-[10px] uppercase tracking-[0.18em] text-orbitra-muted">{unit}</span>
    </span>
  );
}

function Sep() {
  return <span className="text-orbitra-muted/40">|</span>;
}

function TradeCard({
  tag, parties, commodity, amount, finance, highlight, simulatedDays, preOrbitraDays,
}: {
  tag: string; parties: string; commodity: string; amount: string; finance: string; highlight: string;
  simulatedDays: number; preOrbitraDays: number;
}) {
  const t = useTranslations("finalReveal");
  return (
    <div className="glass flex flex-col gap-1 p-3 shadow-[0_0_24px_var(--state-financed-glow)]">
      <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">{tag}</p>
      <p className="font-display text-[12px] font-semibold leading-tight text-orbitra-paper">{parties}</p>
      <p className="text-[10px] text-orbitra-teal-soft">{commodity}</p>
      <div className="flex items-baseline gap-2">
        <span className="font-display text-[15px] font-semibold text-state-financed">{amount}</span>
        <span className="text-[10px] text-orbitra-muted">{finance}</span>
      </div>
      <p className="inline-block self-start rounded-full border border-state-financed/40 bg-state-financed/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-state-financed">
        {highlight}
      </p>
      <p className="font-mono text-[9px] text-orbitra-teal-soft">
        <span className="font-semibold text-state-financed">{simulatedDays}d</span> {t("tradeRowSimulatedSuffix")}
        <span className="px-1 text-orbitra-muted/50">·</span>
        <span className="font-semibold text-state-financed">{preOrbitraDays}d</span> {t("tradeRowPreOrbitraSuffix")}
      </p>
    </div>
  );
}

function PersonaBadge({
  name, full, flag, stats, hint,
}: {
  name: string; full: string; flag: string; stats: Record<string, unknown> | null; hint: string;
}) {
  const tradeCount = stats?.verifiedTrades as number | undefined;
  return (
    <div className="flex items-center gap-2">
      <Monogram name={full} flag={flag} size={32} accent="var(--state-verified)" />
      <div className="flex flex-col">
        <span className="text-[11px] font-semibold text-orbitra-paper">{name}</span>
        <span className="text-[9px] text-orbitra-muted">
          {tradeCount !== undefined ? `${tradeCount} · ${hint}` : hint}
        </span>
      </div>
    </div>
  );
}
